import { useState, useEffect, useRef } from "react";
import {
  IconButton,
  Drawer,
  SwipeableDrawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Box,
  CssBaseline,
  useTheme,
  useMediaQuery,
  Backdrop,
  Toolbar,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  People as StaffIcon,
  Security as GatekeeperIcon,
  Assessment as ReportsIcon,
  Storage as DatabaseIcon,
  Support as SupportIcon,
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";
import AppBarComponent from "./widgets/AppBarComponent";
import { DashboardLayout } from "./DashboardLayout";
import { APP_LOGO, EMPLOYEE, GATEKEEPER } from "../utils/constants";
import { Route, Routes, useNavigate } from "react-router-dom";
import UserProfile from "./profile/UserProfile";
import SupportRequest from "./supports/SupportRequest";
import StaffList from "./staff/StaffList";
import GateKeeper from "./gatekeeper/GateKeeper";
import Reports from "./reports/Reports";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import Settings from "./settings/Settings";
import StaffDetail from "./staff/StaffDetail";
import VisitorRequests from "./requests/VisitorRequests";
import AccountSetup from "./AccountSetup";
import useCompanySettings from "../hooks/useCompanySettings";
import AddStaff from "./staff/AddStaff";
import ImportStaff from "./staff/ImportStaff";

const drawerWidth = 240;
const collapsedWidth = 72;

const menuItems = [
  { text: "Dashboard", link: "/home", icon: <DashboardIcon /> },
  { text: "Staff", link: "/home/staff", icon: <StaffIcon /> },
  { text: "Gatekeeper", link: "/home/gatekeeper", icon: <GatekeeperIcon /> },
  { text: "Reports", link: "/home/reports", icon: <ReportsIcon /> },
  {
    text: "Support/Requests",
    link: "/home/support",
    icon: <SupportIcon />,
  },
  {
    text: "Settings",
    link: "/home/settings",
    icon: <SettingsOutlinedIcon />,
  },
];

export const HomeDash = () => {
  const navigate = useNavigate();
  const { data: settingsData } = useCompanySettings();
  const [open, setOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const drawerRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setOpen(!open);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const toggleButton = document.querySelector(".menu-toggle-button");
      if (toggleButton && toggleButton.contains(event.target)) {
        return;
      }

      if (drawerRef.current && !drawerRef.current.contains(event.target)) {
        if (!isMobile && open) {
          setOpen(false);
        }
        if (isMobile && mobileOpen) {
          setMobileOpen(false);
        }
      }
    };

    if (open || mobileOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, mobileOpen, isMobile]);

  const handleNavigation = (link) => {
    navigate(link);
    if (isMobile) {
      handleDrawerToggle();
    }
  };

  const drawerContent = (
    <Box
      ref={drawerRef}
      sx={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          p: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
          minHeight: 64,
          boxSizing: "border-box",
        }}
      >
        {open && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: 40,
              mx: 1,
              overflow: "hidden",
            }}
          >
            {settingsData && settingsData?.settings?.bnLogo && (
              <img
                src={settingsData?.settings?.bnLogo}
                alt="App Logo"
                style={{
                  height: "100%",
                  width: "100%",
                  objectFit: "cover",
                }}
              />
            )}
          </Box>
        )}
        <IconButton onClick={handleDrawerToggle}>
          {isMobile ? (
            <ChevronLeft />
          ) : open ? (
            <ChevronLeft />
          ) : (
            <ChevronRight />
          )}
        </IconButton>
      </Box>
      <List sx={{ p: 1, flexGrow: 1, overflowY: "auto" }}>
        {menuItems.map((item) => (
          <Tooltip
            key={item.text}
            title={!isMobile && !open ? item.text : ""}
            placement="right"
            arrow
            disableHoverListener={isMobile || open}
          >
            <ListItemButton
              onClick={() => handleNavigation(item.link)}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                minHeight: 48,
                justifyContent: isMobile || open ? "flex-start" : "center",
                px: 2.5,
                "&:hover": {
                  backgroundColor: "action.selected",
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: isMobile || open ? 2 : 0,
                  justifyContent: "center",
                  color: "text.primary",
                }}
              >
                {item.icon}
              </ListItemIcon>
              {(isMobile || open) && (
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    variant: "body2",
                    noWrap: true,
                  }}
                />
              )}
            </ListItemButton>
          </Tooltip>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      <CssBaseline />
      <AppBarComponent
        isMobile={isMobile}
        open={open}
        drawerWidth={drawerWidth}
        collapsedWidth={collapsedWidth}
        handleDrawerToggle={handleDrawerToggle}
      />

      {/* Desktop Drawer */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            width: open ? drawerWidth : collapsedWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: open ? drawerWidth : collapsedWidth,
              overflowX: "hidden",
              transition: theme.transitions.create("width", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
              boxSizing: "border-box",
              position: "fixed",
              height: "100vh",
              overflowY: "hidden", // Prevent drawer from scrolling
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Mobile Drawer */}
      {isMobile && (
        <>
          <Backdrop
            open={mobileOpen}
            onClick={() => setMobileOpen(false)}
            sx={{
              zIndex: theme.zIndex.drawer - 1,
              backgroundColor: "rgba(0,0,0,0.5)",
            }}
          />
          <SwipeableDrawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            onOpen={() => setMobileOpen(true)}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              "& .MuiDrawer-paper": {
                width: drawerWidth,
              },
            }}
          >
            {drawerContent}
          </SwipeableDrawer>
        </>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: "100%",
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          ml: !isMobile
            ? open
              ? `${drawerWidth}px`
              : `${collapsedWidth}px`
            : 0,
          transition: theme.transitions.create("margin", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar />
        <Box
          sx={{
            flexGrow: 1,
            p: 3,
            overflowY: "auto", // Enable vertical scrolling
            overflowX: "hidden", // Prevent horizontal scrolling
          }}
        >
          <Routes>
            <Route path="" element={<DashboardLayout />} />
            <Route path="profile" element={<UserProfile />} />
            <Route path="staff/:id" element={<StaffDetail />} />
            <Route path="account-setup" element={<AccountSetup />} />
            <Route path="support" element={<SupportRequest />} />
            <Route path="staff" element={<StaffList type={EMPLOYEE} />} />
            <Route path="add-staff-manually/:type" element={<AddStaff />} />
            <Route path="import-staff-excel/:type" element={<ImportStaff />} />
            <Route path="visitor-requests" element={<VisitorRequests />} />
            <Route
              path="pending-requests"
              element={<VisitorRequests status="pending" />}
            />
            <Route
              path="gatekeeper"
              element={<StaffList type={GATEKEEPER} />}
            />
            <Route path="reports" element={<Reports />} />
            <Route path="settings" element={<Settings />} />
          </Routes>
        </Box>
      </Box>
    </Box>
  );
};
